import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import { Dispatch, SetStateAction, useRef, useState } from "react";
import { enqueueSnackbar } from "notistack";
import SignatureCanvas from "react-signature-canvas";
import DisplayErrors from "src/components/display-errors";
import Iconify from "src/components/iconify/iconify";
import SimpleDialog from "src/components/simple-dialog";
import TruxSimpleTextField from "src/components/inputs/trux-simple-text-field";
import { ModelSavingData } from "src/hooks/use-model-saving-data";
import { RentalCustomer, RentalOrganizationSettings, RentalTrailerEvent } from "src/models";
import axios, { endpoints, formatEndpoint } from "src/utils/axios";
import { Constants } from "src/constants";
import { RentalTrailerEventForm, submitRentalTrailerEvent } from "./rental-trailer-event-form";

type RentalTrailerEventModalProps = {
  eventModel: ModelSavingData<RentalTrailerEvent>;
  rentalCustomers: RentalCustomer[];
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  onSaved: (event: RentalTrailerEvent) => void;
  outboundEvent?: RentalTrailerEvent;
  organizationSettings?: RentalOrganizationSettings;
};

export function RentalTrailerEventModal({
  eventModel,
  rentalCustomers,
  isOpen,
  setIsOpen,
  onSaved,
  outboundEvent,
  organizationSettings,
}: RentalTrailerEventModalProps) {
  const errorsRef = useRef<HTMLDivElement>(null);
  const sigCanvas = useRef<SignatureCanvas>(null);
  const customerSigCanvas = useRef<SignatureCanvas>(null);
  const [completeOutbound, setCompleteOutbound] = useState(true);
  const [confirmSendEmail, setConfirmSendEmail] = useState(true);
  const [signOnSite, setSignOnSite] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const event = eventModel.model;
  if (!event) {
    return null;
  }

  const isOut = event.rentalTrailerEvent === Constants.RENTAL_TRAILER_EVENT_TYPE_OUT;
  const title = isOut ? "Rent Out" : "Return";
  // a sent/signed form can no longer be edited, so there is nothing to submit
  const submitBlocked =
    (!eventModel.isNew &&
      event.rentalTrailerEventStatus !== Constants.RENTAL_TRAILER_EVENT_STATUS_DRAFT) ||
    (event.rentalTrailerEvent === Constants.RENTAL_TRAILER_EVENT_TYPE_IN && !!event.confirmedAt);
  const canSendContract =
    isOut &&
    !eventModel.isNew &&
    event.rentalTrailerEventStatus === Constants.RENTAL_TRAILER_EVENT_STATUS_DRAFT;
  const awaitingConfirmation =
    isOut &&
    !eventModel.isNew &&
    event.rentalTrailerEventStatus === Constants.RENTAL_TRAILER_EVENT_STATUS_AWAITING_CONFIRMATION;
  // a return keeps the rental open (and listed on the customer) until staff
  // completes it
  const canComplete = !isOut && !eventModel.isNew && !event.confirmedAt;

  const reportActionError = (error: any, fallback: string) => {
    if (error?.response?.status === 422 && error.response.data?.length) {
      error.response.data.forEach((validation: { message: string }) =>
        enqueueSnackbar(validation.message, { variant: "error" })
      );
    } else {
      enqueueSnackbar(fallback, { variant: "error" });
    }
  };

  const confirmEvent = () => {
    if (saving) {
      return;
    }
    setSaving(true);
    axios
      .post(formatEndpoint(endpoints.rentalTrailerEvents.confirm, { eventId: event.id }))
      .then((response) => {
        enqueueSnackbar("Rental confirmed — the signed contract has been emailed to the Customer");
        onSaved(response.data as RentalTrailerEvent);
        setIsOpen(false);
      })
      .catch((error) => {
        reportActionError(error, "The rental could not be confirmed. Please try again.");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const completeEvent = () => {
    if (saving) {
      return;
    }
    setSaving(true);
    axios
      .post(formatEndpoint(endpoints.rentalTrailerEvents.complete, { eventId: event.id }))
      .then((response) => {
        enqueueSnackbar("Rental completed");
        onSaved(response.data as RentalTrailerEvent);
        setIsOpen(false);
      })
      .catch((error) => {
        reportActionError(error, "The rental could not be completed. Please try again.");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const rejectEvent = () => {
    if (saving) {
      return;
    }
    setSaving(true);
    axios
      .post(formatEndpoint(endpoints.rentalTrailerEvents.reject, { eventId: event.id }), {
        reason: rejectReason,
      })
      .then((response) => {
        enqueueSnackbar("The Customer has been asked to sign again");
        onSaved(response.data as RentalTrailerEvent);
        setRejectOpen(false);
        setIsOpen(false);
      })
      .catch((error) => {
        reportActionError(error, "The submission could not be rejected. Please try again.");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const sendContract = () => {
    if (saving) {
      return;
    }
    setSaving(true);
    axios
      .post(formatEndpoint(endpoints.rentalTrailerEvents.sendContract, { eventId: event.id }))
      .then((response) => {
        enqueueSnackbar("The Rental Agreement has been emailed to the Customer");
        onSaved(response.data as RentalTrailerEvent);
        setIsOpen(false);
      })
      .catch((error) => {
        if (error?.response?.status === 422 && error.response.data?.length) {
          error.response.data.forEach((validation: { message: string }) =>
            enqueueSnackbar(validation.message, { variant: "error" })
          );
        } else {
          enqueueSnackbar("The Agreement could not be sent. Please try again.", {
            variant: "error",
          });
        }
      })
      .finally(() => {
        setSaving(false);
      });
  };

  // waits a tick so the error alert is rendered before scrolling it into view
  const scrollToValidation = () => {
    setTimeout(() => {
      errorsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const saveEvent = async () => {
    if (saving) {
      return;
    }
    setSaving(true);
    try {
      const finalizeOnSite = isOut && eventModel.isNew && completeOutbound && signOnSite;
      const sendEmail =
        isOut && eventModel.isNew && completeOutbound && confirmSendEmail && !finalizeOnSite;
      const savedEvent = await submitRentalTrailerEvent({
        eventModel,
        sigCanvas: sigCanvas.current,
        customerSigCanvas: customerSigCanvas.current,
        sendEmail,
        signOnSite: finalizeOnSite,
        requireOutboundComplete: isOut && eventModel.isNew && completeOutbound,
        organizationSettings,
      });
      if (!savedEvent) {
        scrollToValidation();
        return;
      }
      if (!sendEmail && !finalizeOnSite) {
        enqueueSnackbar(`Successfully saved the ${title} form`);
      }
      onSaved(savedEvent);
      setIsOpen(false);
    } catch (error) {
      // validation errors render through DisplayErrors
      scrollToValidation();
    } finally {
      setSaving(false);
    }
  };

  return (
    <SimpleDialog
      title={<>{title}</>}
      open={isOpen}
      size="medium"
      onCancel={() => {
        setIsOpen(false);
      }}
      onSubmit={submitBlocked ? undefined : saveEvent}
      submitDisabled={eventModel.loading || saving}
      submitButton="Save"
      submitButtonIcon={<Iconify icon="basil:save-solid" />}
      submitButtonColor="info"
      buttons={
        (canSendContract && (
          <Button
            variant="contained"
            color="info"
            disabled={saving}
            startIcon={<Iconify icon="ic:round-email" />}
            onClick={sendContract}
          >
            Send Contract
          </Button>
        )) ||
        (awaitingConfirmation && (
          <Stack direction="row" gap={1}>
            <Button
              variant="outlined"
              color="error"
              disabled={saving}
              startIcon={<Iconify icon="mingcute:close-line" />}
              onClick={() => setRejectOpen(true)}
            >
              Reject
            </Button>
            <Button
              variant="contained"
              color="success"
              disabled={saving}
              startIcon={<Iconify icon="eva:checkmark-fill" />}
              onClick={confirmEvent}
            >
              Confirm Rental
            </Button>
          </Stack>
        )) ||
        (canComplete && (
          <Button
            variant="contained"
            color="success"
            disabled={saving}
            startIcon={<Iconify icon="eva:checkmark-fill" />}
            onClick={completeEvent}
          >
            Complete Rental
          </Button>
        )) ||
        undefined
      }
    >
      <div ref={errorsRef}>
        <DisplayErrors savingData={eventModel} />
      </div>

      <RentalTrailerEventForm
        eventModel={eventModel}
        rentalCustomers={rentalCustomers}
        sigCanvas={sigCanvas}
        customerSigCanvas={customerSigCanvas}
        completeOutbound={completeOutbound}
        setCompleteOutbound={setCompleteOutbound}
        confirmSendEmail={confirmSendEmail}
        setConfirmSendEmail={setConfirmSendEmail}
        signOnSite={signOnSite}
        setSignOnSite={setSignOnSite}
        outboundEvent={outboundEvent}
        organizationSettings={organizationSettings}
      />

      <Dialog open={rejectOpen} onClose={() => !saving && setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject this submission?</DialogTitle>
        <DialogContent>
          <TruxSimpleTextField
            label="Reason (sent to the Customer)"
            value={rejectReason}
            onChange={setRejectReason}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={rejectEvent} disabled={saving}>
            Reject &amp; Ask to Re-sign
          </Button>
        </DialogActions>
      </Dialog>
    </SimpleDialog>
  );
}
